/* eslint-disable no-magic-numbers, @typescript-eslint/ban-ts-comment -- source-parity utility port */
// src/utils/hex.utils.ts
// ============= GENERAL HEX CONVERSION UTILITIES =============

import { fromString as u8FromString } from 'uint8arrays/from-string';

import { HEX_RADIX, HEX_PAD_LENGTH, HEX_PAD_CHAR } from '@/constants/crypto.constants';

/**
 * Pure hex conversion utilities
 * These are general-purpose functions not specific to cryptography
 */

/**
 * Convert hex string to Uint8Array
 * @param hex The hex string to convert (e.g., "deadbeef")
 * @returns The converted Uint8Array
 * @throws Error if hex string is invalid
 */
export function hexToBytes(hex: string): Uint8Array {
  validateHexString(hex);

  const bytes = new Uint8Array(hex.length / HEX_PAD_LENGTH);

  for (let i = 0; i < hex.length; i += HEX_PAD_LENGTH) {
    bytes[i / HEX_PAD_LENGTH] = parseInt(
      hex.substring(i, i + HEX_PAD_LENGTH),
      HEX_RADIX
    );
  }

  return bytes;
}

/**
 * Convert Uint8Array to hex string
 * @param data The data to convert
 * @returns The hex string representation (e.g., "deadbeef")
 */
export function bytesToHex(data: Uint8Array): string {
  return Array.from(data)
    .map(byte => byte.toString(HEX_RADIX).padStart(HEX_PAD_LENGTH, HEX_PAD_CHAR))
    .join('');
}

/**
 * Convert number array to Uint8Array
 * Useful for parsing API responses like "[174,56,32,163,...]"
 * @param numbers Array of numbers (0-255)
 * @returns The converted Uint8Array
 * @throws Error if any number is out of range
 */
export function numbersToBytes(numbers: number[]): Uint8Array {
  // Validate all numbers are valid bytes
  for (const num of numbers) {
    if (!Number.isInteger(num) || num < 0 || num > 255) {
      throw new Error(`Invalid byte value: ${num}. Must be integer 0-255.`);
    }
  }

  return new Uint8Array(numbers);
}

/**
 * Convert Uint8Array to number array
 * @param data The data to convert
 * @returns Array of numbers (0-255)
 */
export function bytesToNumbers(data: Uint8Array): number[] {
  return Array.from(data);
}

/**
 * Parse JSON-encoded number array string to Uint8Array
 * Handles API responses like '"[174,56,32,163,...]"'
 * @param jsonArrayString JSON string containing number array
 * @returns The converted Uint8Array
 * @throws Error if JSON is invalid or contains invalid numbers
 */
export function parseJsonArrayToBytes(jsonArrayString: string): Uint8Array {
  try {
    const parsed: unknown = JSON.parse(jsonArrayString);

    if (!Array.isArray(parsed) || !parsed.every(item => typeof item === 'number')) {
      throw new Error('Parsed JSON is not an array of numbers');
    }

    return numbersToBytes(parsed);
  } catch (error) {
    throw new Error(`Failed to parse JSON array: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Convert Uint8Array to JSON-encoded number array string
 * @param data The data to convert
 * @returns JSON string like "[174,56,32,163,...]"
 */
export function bytesToJsonArray(data: Uint8Array): string {
  return JSON.stringify(Array.from(data));
}

/**
 * Validate hex string format
 * @param hex The hex string to validate
 * @throws Error if format is invalid
 */
export function validateHexString(hex: string): void {
  if (typeof hex !== 'string') {
    throw new Error('Hex input must be a string');
  }

  if (hex.length % HEX_PAD_LENGTH !== 0) {
    throw new Error('Hex string must have an even number of characters');
  }

  if (!/^[0-9a-fA-F]*$/.test(hex)) {
    throw new Error('Hex string contains invalid characters. Only 0-9, a-f, A-F allowed.');
  }
}

/**
 * Check if string is valid hex format
 * @param hex The string to check
 * @returns True if valid hex format
 */
export function isValidHex(hex: string): boolean {
  try {
    validateHexString(hex);
    return true;
  } catch {
    return false;
  }
}

/**
 * Normalize hex string (lowercase, no spaces)
 * @param hex The hex string to normalize
 * @returns Normalized hex string
 */
export function normalizeHex(hex: string): string {
  return hex.replace(/\s+/g, '').toLowerCase();
}

/**
 * Format hex string for display (add spaces every 2 characters)
 * @param hex The hex string to format
 * @param separator The separator to use (default: space)
 * @returns Formatted hex string (e.g., "de ad be ef")
 */
export function formatHexForDisplay(hex: string, separator = ' '): string {
  validateHexString(hex);
  const matches = hex.match(/.{2}/g);
  return matches !== null ? matches.join(separator) : '';
}

/**
 * Compare two hex strings for equality (case-insensitive)
 * @param hex1 First hex string
 * @param hex2 Second hex string
 * @returns True if hex strings represent same data
 */
export function compareHex(hex1: string, hex2: string): boolean {
  try {
    return normalizeHex(hex1) === normalizeHex(hex2);
  } catch {
    return false;
  }
}

// ============= TYPE GUARDS =============

/**
 * Type guard to check if value is a valid Uint8Array
 * @param value The value to check
 * @returns True if value is Uint8Array
 */
export function isUint8Array(value: unknown): value is Uint8Array {
  return value instanceof Uint8Array;
}

/**
 * Type guard to check if value is a valid number array for bytes
 * @param value The value to check
 * @returns True if value is array of valid byte numbers
 */
export function isValidByteArray(value: unknown): value is number[] {
  return Array.isArray(value) &&
         value.every(item => Number.isInteger(item) && item >= 0 && item <= 255);
}

/**
 * Converts a base64 string back to a PSK Uint8Array
 */
export function base64ToPsk(base64: string): Uint8Array {
  return u8FromString(base64, 'base64');     // ✅ works in browser
}

export const SWARM_KEY_STRING =
  "/key/swarm/psk/1.0.0/\\n/base16/\\ndffb7e3135399a8b1612b2aaca1c36a3a8ac2cd0cca51ceeb2ced87d308cac6d";

export const SWARM_KEY_BASE64 = ""; // if you prefer base64, fill this in

// Convert a JSON-escaped or raw string swarm key into a Uint8Array.
// Accepts either a string containing actual newlines or an escaped form (\\n).
// Valid lengths are commonly 95 (file-style) or 32 (raw key bytes); adjust if needed.
export function parseSwarmKeyFromString(swarmKeyStr: string): Uint8Array {
  // Normalize escaped newlines (so both "/...\\n..." and "/...\n..." work)
  const normalized = swarmKeyStr.replace(/\\r\\n/g, '\n').replace(/\\n/g, '\n');

  // Encode into bytes (UTF-8)
  const bytes = new TextEncoder().encode(normalized);

  if (bytes.length !== 95 && bytes.length !== 32) {
    throw new Error(
      `Invalid swarm key length: ${bytes.length}. Expected 95 (swarm.key file) or 32 (raw key bytes).`
    );
  }

  return bytes;
}

// Decode a base64-encoded swarm key to Uint8Array.
// Works in both browser environments and Node.js.
export function parseSwarmKeyFromBase64(base64: string): Uint8Array {
  // Node.js fallback if atob is not available
  if (typeof atob === 'undefined') {
    // @ts-ignore - Buffer exists in Node
    const buf = Buffer.from(base64, 'base64');
    const arr = new Uint8Array(buf.byteLength);
    arr.set(buf);
    if (arr.length !== 95 && arr.length !== 32) {
      throw new Error(
        `Invalid swarm key length after base64 decode: ${arr.length}. Expected 95 or 32.`
      );
    }
    return arr;
  }

  // Browser method
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  if (bytes.length !== 95 && bytes.length !== 32) {
    throw new Error(
      `Invalid swarm key length after base64 decode: ${bytes.length}. Expected 95 or 32.`
    );
  }

  return bytes;
}
